import Animation from "@/Shared/Animations/Animation";
import { useDynamicHeight } from "@/Shared/Hooks/useDynamicHeight";
import DynamicSpacer from "@/Shared/Layout/CustomComponents/DynamicSpacer";
import PageTitle from "@/Shared/Layout/PageTitle";
import { displayNameForCarrier } from "@/Data/displayNameForCarrier";
import { useOnce } from "@/Hooks/useOnce";
import useQuery from "@/Hooks/useQuery";
import { useShipmentService } from "@/Services/ShipmentService";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageLoadingSpinner from "@/Shared/Components/CustomComponents/PageLoadingSpinner";

function ShipmentBookedScreen() {
  const query = useQuery();
  const navigate = useNavigate();

  const shipmentsService = useShipmentService();

  // query-string can return arrays/null for repeated params; ids are single values
  const shipmentIdParam = query["shipmentId"];
  const shipmentId =
    typeof shipmentIdParam === "string" ? shipmentIdParam : undefined;

  const [shipment, setShipment] = useState<Shipment | undefined>();

  async function loadShipment() {
    // A missing shipmentId could never load, so head home (matches the catch)
    if (shipmentId === undefined) {
      navigate("/");
      return;
    }
    try {
      const response = await shipmentsService.getShipment(shipmentId);

      setShipment(response.shipment);
    } catch {
      navigate("/");
    }
  }

  const animationHeight = useDynamicHeight(300, 350);

  useOnce(async () => {
    loadShipment();
  });

  if (shipment === undefined) {
    return <PageLoadingSpinner />;
  }

  const { selectedQuote, pickupDate } = shipment;

  if (selectedQuote === undefined) {
    // Should never get here
    throw new Error("Missing selectedQuote");
  }

  const { carrierIdentifier } = selectedQuote;

  const carrierDisplayName = displayNameForCarrier(carrierIdentifier);

  function onClick() {
    navigate("/view-shipment?shipmentId=" + shipmentId);
  }

  return (
    <>
      <PageTitle>Shipment Booked</PageTitle>
      {/* Clears the navbar. Fixed 48px — Stack's gap scale has no 48 step, and
          PageTitle above renders no element for a gap to apply to. */}
      <DynamicSpacer minHeight={48} maxHeight={48} />

      <Stack gap={4} align="center">
        <Stack gap={2} align="center">
          {/* Replica of the legacy Cargo Heading2 (24px bold). */}
          <Heading level={2}>Congratulations! Your Shipment is Booked!</Heading>
          {/* Replica of the legacy Cargo Description (18px light, centered). */}
          <T size="lg" weight="light" align="center">
            {carrierDisplayName} will pick up your freight on{" "}
            <strong>
              <T weight="bold">
                {moment(pickupDate).format("dddd Do MMMM YYYY")}
              </T>
            </strong>
            .
          </T>
        </Stack>

        <Animation
          pathToAnimation="/animations/success.json"
          lottieFilesUrl="https://edit.lottiefiles.com/?src=https%3A%2F%2Fassets7.lottiefiles.com%2Fprivate_files%2Flf30_P2wyYh.json"
          loop={true}
          height={animationHeight}
        />

        {/* Replica of the legacy Cargo Microcopy (16px grey text). */}
        <T size="base" color="secondary">
          Before the driver arrives, you must print your pickup package.
          We&apos;ll explain that next.
        </T>
        <Button primary onClick={onClick} size="large" nextArrow>
          View Shipment
        </Button>
      </Stack>
    </>
  );
}

export default ShipmentBookedScreen;
